import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#64748b'];

interface ChartWidgetProps {
  data: any;
  config: Record<string, any>;
  loading: boolean;
  error: string | null;
  widgetType: string;
  width: number;
  height: number;
}

const CHART_TITLES: Record<string, string> = {
  chart_monthly_sales: 'Vendas Mensais',
  chart_conversion_funnel: 'Funil de Conversao',
  chart_leads_trend: 'Tendencia de Leads',
  chart_revenue_trend: 'Tendencia de Receita',
  chart_top_products: 'Top Produtos',
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' }).format(value);

const ChartWidget: React.FC<ChartWidgetProps> = ({ data, loading, error, widgetType, height }) => {
  const title = CHART_TITLES[widgetType] || 'Grafico';
  const chartHeight = Math.max(height - 60, 120);

  if (loading) {
    return (
      <ChartShell title={title}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight }}>
          <div style={{ width: '24px', height: '24px', border: '3px solid var(--border-light)', borderTopColor: 'var(--color-primary-600)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
        </div>
      </ChartShell>
    );
  }

  if (error || !data) {
    return (
      <ChartShell title={title}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight, color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
          {error || 'Sem dados'}
        </div>
      </ChartShell>
    );
  }

  return (
    <ChartShell title={title}>
      <div style={{ width: '100%', height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart(widgetType, data)}
        </ResponsiveContainer>
      </div>
    </ChartShell>
  );
};

function ChartShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 'var(--space-4)' }}>
      <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', margin: 0, marginBottom: 'var(--space-3)' }}>
        {title}
      </h3>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

function renderChart(widgetType: string, data: any): React.ReactElement {
  const tooltipStyle = { backgroundColor: 'var(--glass-bg-strong)', border: '1px solid var(--glass-border)', borderRadius: '8px', fontSize: '12px' };

  switch (widgetType) {
    case 'chart_monthly_sales': {
      const monthlyData = data?.monthly_data || [];
      return (
        <BarChart data={monthlyData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--glass-border)" />
          <XAxis dataKey="month_name" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCurrency(v)} />
          <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => formatCurrency(value)} />
          <Bar dataKey="total_sales" fill="#6366f1" radius={[4, 4, 0, 0]} name="Vendas" />
        </BarChart>
      );
    }

    case 'chart_conversion_funnel': {
      const stageData = data?.stage_data || [];
      return (
        <BarChart data={stageData} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="var(--glass-border)" />
          <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
          <YAxis dataKey="stage_name" type="category" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} width={100} />
          <Tooltip contentStyle={tooltipStyle} />
          <Bar dataKey="opportunity_count" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Oportunidades" />
        </BarChart>
      );
    }

    case 'chart_leads_trend':
    case 'chart_revenue_trend': {
      const chartData = data?.data || [];
      const dataKey = widgetType === 'chart_revenue_trend' ? 'value' : 'count';
      const color = widgetType === 'chart_revenue_trend' ? '#10b981' : '#6366f1';
      return (
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--glass-border)" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false}
            tickFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth() + 1}`; }} />
          <YAxis tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false}
            tickFormatter={widgetType === 'chart_revenue_trend' ? (v) => formatCurrency(v) : undefined} />
          <Tooltip contentStyle={tooltipStyle}
            labelFormatter={(v) => new Date(v).toLocaleDateString('pt-BR')}
            formatter={widgetType === 'chart_revenue_trend' ? (value: number) => formatCurrency(value) : undefined} />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} name={widgetType === 'chart_revenue_trend' ? 'Receita' : 'Leads'} />
        </LineChart>
      );
    }

    case 'chart_top_products': {
      const products = data?.products || [];
      const pieData = products.slice(0, 6).map((p: any, i: number) => ({
        name: p.product_name || p.name || `Produto ${i + 1}`,
        value: p.total_revenue || p.total_quantity || 0,
      }));
      return (
        <PieChart>
          <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="40%" outerRadius="70%" paddingAngle={2}>
            {pieData.map((_: any, i: number) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: '11px' }} />
        </PieChart>
      );
    }

    default:
      return <BarChart data={[]}><Bar dataKey="value" /></BarChart>;
  }
}

export default ChartWidget;
